import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Clock, Loader2, Send } from "lucide-react";

import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const ALL_PLATFORMS = ["tiktok", "instagram", "facebook"];
const PLATFORM_LABEL = {
    tiktok: "TikTok",
    instagram: "Instagram",
    facebook: "Facebook",
};

const HOURS_12 = Array.from({ length: 12 }, (_, i) => i + 1); // 1..12
const MINUTES = ["00", "15", "30", "45"];

export default function SchedulePanel({
    onSubmit,
    scheduling,
    defaultPlatforms = [],
    seedSlot = null,
    slot = null,
    onScheduleChange = null,
    preSubmitRow = null,
}) {
    const [date, setDate] = useState(() => {
        const d = new Date();
        d.setMinutes(0, 0, 0);
        d.setHours(d.getHours() + 1);
        return d;
    });
    const [hour12, setHour12] = useState(() => {
        const h = new Date().getHours() + 1;
        const h12 = ((h + 11) % 12) + 1;
        return h12;
    });
    const [minute, setMinute] = useState("00");
    const [meridiem, setMeridiem] = useState(() =>
        new Date().getHours() + 1 >= 12 ? "PM" : "AM"
    );
    const [platforms, setPlatforms] = useState(() => {
        const unique = Array.from(new Set(defaultPlatforms.filter(Boolean)));
        return unique.length ? unique : ["tiktok", "instagram", "facebook"];
    });

    // Agent 2 "Use this" — externally seed the slot
    useEffect(() => {
        if (!seedSlot) return;
        if (seedSlot.date instanceof Date) setDate(seedSlot.date);
        if (typeof seedSlot.hour12 === "number") setHour12(seedSlot.hour12);
        if (typeof seedSlot.minute === "string") setMinute(seedSlot.minute);
        if (seedSlot.meridiem === "AM" || seedSlot.meridiem === "PM") {
            setMeridiem(seedSlot.meridiem);
        }
        if (Array.isArray(seedSlot.platforms) && seedSlot.platforms.length > 0) {
            setPlatforms(seedSlot.platforms);
        }
    }, [seedSlot]);

    const scheduledFor = useMemo(() => {
        const d = new Date(date);
        let h = hour12 % 12;
        if (meridiem === "PM") h += 12;
        d.setHours(h, parseInt(minute, 10), 0, 0);
        return d;
    }, [date, hour12, minute, meridiem]);

    const isFuture = scheduledFor.getTime() > Date.now();

    // Bubble schedule state up so the floating Adsum drawer (rendered
    // outside this panel) can read scheduled_for + platforms in real time.
    useEffect(() => {
        if (!onScheduleChange) return;
        onScheduleChange({
            scheduledFor: scheduledFor.toISOString(),
            platforms,
        });
    }, [scheduledFor, platforms, onScheduleChange]);

    const togglePlatform = (p) => {
        setPlatforms((s) => (s.includes(p) ? s.filter((x) => x !== p) : [...s, p]));
    };

    const submit = () => {
        if (!isFuture) return;
        if (platforms.length === 0) return;
        onSubmit({
            scheduledFor: scheduledFor.toISOString(),
            platforms,
        });
    };

    return (
        <motion.section
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            transition={{ duration: 0.45, ease: [0.25, 0.1, 0.25, 1] }}
            className="flex flex-col gap-6 rounded-2xl border border-[#1F2A40] bg-[#111726] p-7"
            data-testid="schedule-panel"
        >
            <div className="flex flex-col gap-1">
                <span className="text-[10px] uppercase tracking-[0.32em] text-[#8A93A6]">
                    schedule
                </span>
                <h2 className="text-xl font-medium tracking-tight text-[#E8ECF5]">
                    When should this drop?
                </h2>
            </div>

            <div className="grid gap-5 md:grid-cols-2">
                <Field label="Date">
                    <Popover>
                        <PopoverTrigger asChild>
                            <button
                                type="button"
                                className="flex w-full items-center justify-between rounded-lg border border-[#1F2A40] bg-[#0B0F19] px-4 py-2.5 text-sm text-[#E8ECF5] transition-colors hover:border-[#00F5D4]/50 focus:border-[#00F5D4] focus:outline-none"
                                data-testid="schedule-date-trigger"
                            >
                                <span>{format(date, "EEE, MMM d, yyyy")}</span>
                                <CalendarIcon className="h-4 w-4 text-[#8A93A6]" />
                            </button>
                        </PopoverTrigger>
                        <PopoverContent
                            align="start"
                            className="w-auto border-[#1F2A40] bg-[#111726] p-0"
                            data-testid="schedule-date-popover"
                        >
                            <Calendar
                                mode="single"
                                selected={date}
                                onSelect={(d) => d && setDate(d)}
                                disabled={(d) => {
                                    const t = new Date();
                                    t.setHours(0, 0, 0, 0);
                                    return d < t;
                                }}
                            />
                        </PopoverContent>
                    </Popover>
                </Field>

                <Field label="Time">
                    <div
                        className="flex items-center gap-1 rounded-lg border border-[#1F2A40] bg-[#0B0F19] px-3 py-2 text-sm text-[#E8ECF5]"
                        data-testid="schedule-time-row"
                    >
                        <Clock className="mr-1 h-4 w-4 text-[#8A93A6]" />
                        <select
                            value={hour12}
                            onChange={(e) => setHour12(parseInt(e.target.value, 10))}
                            className="bg-transparent text-sm text-[#E8ECF5] outline-none"
                            data-testid="schedule-hour-select"
                        >
                            {HOURS_12.map((h) => (
                                <option key={h} value={h} className="bg-[#111726]">
                                    {h}
                                </option>
                            ))}
                        </select>
                        <span className="text-[#8A93A6]">:</span>
                        <select
                            value={minute}
                            onChange={(e) => setMinute(e.target.value)}
                            className="bg-transparent text-sm text-[#E8ECF5] outline-none"
                            data-testid="schedule-minute-select"
                        >
                            {MINUTES.map((m) => (
                                <option key={m} value={m} className="bg-[#111726]">
                                    {m}
                                </option>
                            ))}
                        </select>
                        <div className="ml-auto inline-flex overflow-hidden rounded-md border border-[#1F2A40]">
                            {["AM", "PM"].map((m) => (
                                <button
                                    type="button"
                                    key={m}
                                    onClick={() => setMeridiem(m)}
                                    className={[
                                        "px-2.5 py-1 text-xs font-medium transition-colors",
                                        meridiem === m
                                            ? "bg-[#00F5D4] text-[#0B0F19]"
                                            : "text-[#8A93A6] hover:text-[#E8ECF5]",
                                    ].join(" ")}
                                    data-testid={`schedule-meridiem-${m.toLowerCase()}`}
                                >
                                    {m}
                                </button>
                            ))}
                        </div>
                    </div>
                </Field>
            </div>

            <Field label="Platforms">
                <div
                    className="flex flex-wrap gap-2"
                    data-testid="schedule-platforms-row"
                >
                    {ALL_PLATFORMS.map((p) => {
                        const active = platforms.includes(p);
                        return (
                            <button
                                key={p}
                                type="button"
                                onClick={() => togglePlatform(p)}
                                className={[
                                    "rounded-full border px-4 py-1.5 text-sm font-medium transition-all",
                                    active
                                        ? "border-[#00F5D4] bg-[#00F5D4]/10 text-[#00F5D4] shadow-[0_0_12px_rgba(0,245,212,0.15)]"
                                        : "border-[#1F2A40] bg-[#0B0F19] text-[#8A93A6] hover:border-[#2F58CD]/50 hover:text-[#E8ECF5]",
                                ].join(" ")}
                                data-testid={`schedule-platform-${p}`}
                            >
                                {PLATFORM_LABEL[p]}
                            </button>
                        );
                    })}
                </div>
            </Field>

            {typeof slot === "function"
                ? slot({ scheduledFor: scheduledFor.toISOString(), platforms })
                : slot}

            {preSubmitRow}

            <div className="flex flex-col gap-3 pt-1">
                <button
                    type="button"
                    onClick={submit}
                    disabled={!isFuture || platforms.length === 0 || scheduling}
                    className="group relative w-full overflow-hidden rounded-xl bg-gradient-to-r from-[#2F58CD] to-[#2F58CD] py-3.5 text-sm font-medium text-white transition-all duration-300 hover:shadow-[0_0_28px_rgba(0,245,212,0.4)] disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
                    data-testid="schedule-submit-btn"
                >
                    <span className="relative z-10 inline-flex items-center justify-center gap-2">
                        {scheduling ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Send className="h-4 w-4" />
                        )}
                        {scheduling ? "Marking…" : "Mark as Ready"}
                    </span>
                    <span
                        aria-hidden
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-[#00F5D4]/35 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                    />
                </button>
                {!isFuture && (
                    <p
                        className="text-xs text-[#FF5470]"
                        data-testid="schedule-future-warning"
                    >
                        Pick a time in the future.
                    </p>
                )}
                {platforms.length === 0 && (
                    <p
                        className="text-xs text-[#FF5470]"
                        data-testid="schedule-platforms-warning"
                    >
                        Select at least one platform.
                    </p>
                )}
            </div>
        </motion.section>
    );
}

function Field({ label, children }) {
    return (
        <label className="flex flex-col gap-2">
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#8A93A6]">
                {label}
            </span>
            {children}
        </label>
    );
}
